import { cancelMediaOperation, getMediaOperationToken, isMediaOperationCurrent } from "./mediaStart";
import { refreshAudioSource } from "../services/premiumAudio.js";
class AudioManager {
  constructor() {
    this.context = null;
    this.unlocked = false;

    // id -> { element, gain|null, targetVolume, fadeRaf, forceNative }
    this.elements = new Map();

    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    this._isIOS =
      /iPad|iPhone|iPod/.test(ua) ||
      (ua.includes("Mac") &&
        typeof navigator !== "undefined" &&
        navigator.maxTouchPoints > 1);

    this.nativeMode = this._shouldUseNativeMode();

    // iOS: on garde WebAudio (volumes OK) + on route la sortie vers un stream + "sink audio"
    this._useIOSStreamOutput = false;

    this._streamDest = null; // MediaStreamAudioDestinationNode
    this._streamSink = null; // <audio> caché qui lit streamDest.stream
  }

  _updateIOSStreamFlag() {
    this._useIOSStreamOutput = false;
    // si on bascule en native, on éteint le sink
    if (!this._useIOSStreamOutput) {
      if (this._streamSink) {
        try { this._streamSink.pause(); } catch {}
        try { this._streamSink.srcObject = null; } catch {}
        try { this._streamSink.remove(); } catch {}
        this._streamSink = null;
      }
      if (this._streamDest) {
        try {
          this._streamDest.stream?.getTracks?.().forEach((t) => t.stop());
        } catch {}
        this._streamDest = null;
      }
    }
  }

  _shouldUseNativeMode() {
    if (typeof window === "undefined") return true;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return true;
    return false; // WebAudio dispo => on l'utilise (y compris sur iOS)
  }

  init() {
    if (this.nativeMode) return null;
    if (this.context) return this.context;

    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) {
      console.warn("⚠️ AudioContext not supported → fallback native");
      this.nativeMode = true;
      this._updateIOSStreamFlag();
      return null;
    }

    try {
      this.context = new Ctx();

      const onState = () => {
        const st = this.context?.state;
        // iOS peut passer à "interrupted" en background/verrouillage
        if (st === "suspended" || st === "interrupted") {
          this.context?.resume().catch(() => {});
        }
      };

      // Safari supporte addEventListener, sinon fallback onstatechange
      if (typeof this.context.addEventListener === "function") {
        this.context.addEventListener("statechange", onState);
      } else {
        this.context.onstatechange = onState;
      }

      // (Ré)active iOS stream flag (au cas où nativeMode aurait changé avant)
      this._updateIOSStreamFlag();

      return this.context;
    } catch (err) {
      console.error("❌ Erreur création AudioContext → fallback native:", err);
      this.nativeMode = true;
      this._updateIOSStreamFlag();
      return null;
    }
  }

_getOutputNode(ctx) {
  return ctx?.destination || null;
}

  _ensureStreamSink() {
    if (!this._useIOSStreamOutput) return null;
    if (!this.context) return null;
    if (typeof document === "undefined") return null;

    const out = this._getOutputNode(this.context);
    if (!out?.stream) return null;

    // déjà prêt
    if (this._streamSink && this._streamSink.srcObject === out.stream) {
      return this._streamSink;
    }

    const sink = this._streamSink || document.createElement("audio");
    sink.playsInline = true;
    sink.autoplay = false;
    sink.muted = false;
    sink.loop = true;
    sink.srcObject = out.stream;

    sink.setAttribute("aria-hidden", "true");
    sink.style.display = "none";

    // plus fiable si dans le DOM
    if (!sink.isConnected) {
      try {
        document.body.appendChild(sink);
      } catch {}
    }

    this._streamSink = sink;
    return sink;
  }

  async _ensureStreamSinkPlaying() {
    const sink = this._ensureStreamSink();
    if (!sink) return true;

    if (!sink.paused) return true;

    try {
      const p = sink.play();
      if (p) await p;
      return true;
    } catch {
      // Si pas de gesture, iOS peut refuser.
      // On réessaiera lors d'un play déclenché par l'utilisateur.
      return false;
    }
  }

_attachStreamActivityListeners(el) {
  if (!this._useIOSStreamOutput || !el) return;

  const am = el._audioManager || (el._audioManager = {});
  if (am._streamBound) return;

  const onPlaying = () => {
    this._ensureStreamSinkPlaying().catch(() => {});
  };

  // Pas d'auto-pause du sink : iOS refuse souvent de le relancer
  // hors gesture utilisateur. On le laisse tourner (silence quand
  // aucune source n'y envoie de données) → la session 2 est audible.
  el.addEventListener("playing", onPlaying);

  am._streamBound = true;
  am._onStreamPlaying = onPlaying;
}

_detachStreamActivityListeners(el) {
  const am = el?._audioManager;
  if (!am?._streamBound) return;

  try { el.removeEventListener("playing", am._onStreamPlaying); } catch {}

  delete am._streamBound;
  delete am._onStreamPlaying;
}

  async unlock() {
    if (this.unlocked) return true;

    if (this.nativeMode) {
      this.unlocked = true;
      return true;
    }

    const ctx = this.init();
    if (!ctx) {
      this.nativeMode = true;
      this._updateIOSStreamFlag();
      this.unlocked = true;
      return true;
    }

    try {
      if (ctx.state === "suspended" || ctx.state === "interrupted") {
        await ctx.resume();
      }

      // Buffer silencieux pour "débloquer"
      const buffer = ctx.createBuffer(1, 1, 22050);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);

      // prépare le sink iOS (sans forcer play)
      this._ensureStreamSink();

      this.unlocked = true;
      return true;
    } catch (err) {
      // IMPORTANT: NotAllowedError => souvent hors gesture (useEffect)
      // On ne bascule pas en native sinon on perd les volumes progressifs.
      if (err?.name === "NotAllowedError") {
        return false;
      }

      console.error("❌ Unlock failed → fallback native:", err);
      this.nativeMode = true;
      this._updateIOSStreamFlag();
      this.unlocked = true;
      return true;
    }
  }

  _normalizePlaybackRate(audioElement) {
  if (!audioElement) return;

  try {
    audioElement.defaultPlaybackRate = 1;
    audioElement.playbackRate = 1;
  } catch {}

  try {
    audioElement.preservesPitch = true;
  } catch {}

  try {
    audioElement.webkitPreservesPitch = true;
  } catch {}

  try {
    audioElement.mozPreservesPitch = true;
  } catch {}
}

  setupElement(audioElement, id) {
    if (!audioElement || !id) return null;
    this._normalizePlaybackRate(audioElement);

    // Stop fade en cours si existe
    const existing = this.elements.get(id);
    if (existing?.fadeRaf) {
      cancelAnimationFrame(existing.fadeRaf);
    }

    // Mode natif global
    if (this.nativeMode) {
      this.elements.set(id, {
        element: audioElement,
        gain: null,
        targetVolume: Number(audioElement.volume) || 1,
        fadeRaf: null,
        forceNative: true,
      });
      return null;
    }

    const ctx = this.init();
    if (!ctx) {
      // fallback natif global
      this.elements.set(id, {
        element: audioElement,
        gain: null,
        targetVolume: Number(audioElement.volume) || 1,
        fadeRaf: null,
        forceNative: true,
      });
      return null;
    }

    try {
      // Réutilise câblage existant (ne pas recréer MediaElementSource)
      if (audioElement._audioManager?.ctx === ctx && audioElement._audioManager?.gain) {
        const gain = audioElement._audioManager.gain;

        // S'assure que le gain sort vers la bonne destination (stream sur iOS, sinon destination)
        try { gain.disconnect(); } catch {}
        try { gain.connect(this._getOutputNode(ctx)); } catch {}

        this.elements.set(id, {
          element: audioElement,
          gain,
          targetVolume: gain.gain.value,
          fadeRaf: null,
          forceNative: false,
        });

        // volume de l'élément à 1 (gain gère)
        try { audioElement.volume = 1; } catch {}

        this._attachStreamActivityListeners(audioElement);
        return gain;
      }

      // Nouveau câblage
      const source = ctx.createMediaElementSource(audioElement);
      const gain = ctx.createGain();

      source.connect(gain);
      gain.connect(this._getOutputNode(ctx));

      // merge pour ne pas écraser les flags _streamBound éventuellement
      audioElement._audioManager = {
        ...(audioElement._audioManager || {}),
        source,
        gain,
        ctx,
      };

      this.elements.set(id, {
        element: audioElement,
        gain,
        targetVolume: 1,
        fadeRaf: null,
        forceNative: false,
      });

      try { audioElement.volume = 1; } catch {}

      this._attachStreamActivityListeners(audioElement);
      return gain;
    } catch (err) {
      console.warn(`⚠️ WebAudio setup failed for ${id} → fallback native (per element)`, err);

      // fallback natif par élément (sans forcer global)
      this.elements.set(id, {
        element: audioElement,
        gain: null,
        targetVolume: Number(audioElement.volume) || 1,
        fadeRaf: null,
        forceNative: true,
      });

      return null;
    }
  }

  ensureElement(audioElement, id) {
    if (!audioElement || !id) return;
    const entry = this.elements.get(id);
    if (entry?.element === audioElement) return;
    this.setupElement(audioElement, id);
  }

  setVolume(id, targetVolume, duration = 0) {
    const entry = this.elements.get(id);
    if (!entry) return;

    const el = entry.element;
    const safe = Math.max(0, Math.min(1, Number(targetVolume) || 0));

    // stocke target pour resync
    entry.targetVolume = safe;

    // annule fade RAF natif
    if (entry.fadeRaf) {
      cancelAnimationFrame(entry.fadeRaf);
      entry.fadeRaf = null;
    }

if (this.nativeMode || entry.forceNative || !entry.gain || !this.context) {
  if (!el) return;

  let startVolume = 0;

  try {
    startVolume = el.muted ? 0 : Math.max(0, Math.min(1, Number(el.volume) || 0));
    if (safe > 0) el.muted = false;
  } catch {}

  if (duration <= 0) {
    try {
      el.volume = safe;
      el.muted = safe <= 0;
    } catch {}

    return;
  }

  const delta = safe - startVolume;
  const startedAt = performance.now();

  const step = (now) => {
    const progress = Math.min(1, (now - startedAt) / duration);
    const volume = Math.max(0, Math.min(1, startVolume + delta * progress));

    try {
      el.muted = false;
      el.volume = volume;
    } catch {}

    if (progress < 1) {
      entry.fadeRaf = requestAnimationFrame(step);
      return;
    }

    entry.fadeRaf = null;

    try {
      el.volume = safe;
      el.muted = safe <= 0;
    } catch {}
  };

  entry.fadeRaf = requestAnimationFrame(step);
  return;
}

    // ===== MODE WEBAUDIO =====
    const gain = entry.gain;
    const ctx = this.context;

    try {
const now = ctx.currentTime;

if (typeof gain.gain.cancelAndHoldAtTime === "function") {
  gain.gain.cancelAndHoldAtTime(now);
} else {
  const current = gain.gain.value;
  gain.gain.cancelScheduledValues(now);
  gain.gain.setValueAtTime(current, now);
}

      if (duration <= 0) {
        gain.gain.setValueAtTime(safe, now);
      } else {
        const end = now + duration / 1000;
        gain.gain.linearRampToValueAtTime(safe, end);
      }
    } catch (err) {
      console.warn(`⚠️ setVolume WebAudio failed for ${id}:`, err);
      // fallback natif "simple"
      try { el.volume = safe; } catch {}
    }
  }

  setLiveVolume(id, targetVolume) {
  const entry = this.elements.get(id);
  if (!entry) return;

  const element = entry.element;
  const safeVolume = Math.max(0, Math.min(1, Number(targetVolume) || 0));

  entry.targetVolume = safeVolume;

  if (entry.fadeRaf) {
    cancelAnimationFrame(entry.fadeRaf);
    entry.fadeRaf = null;
  }

  if (this.nativeMode || entry.forceNative || !entry.gain || !this.context) {
    if (!element) return;

    try {
      element.muted = false;
      element.volume = safeVolume;
      if (safeVolume <= 0) element.muted = true;
    } catch {}

    return;
  }

  const parameter = entry.gain.gain;
  const now = this.context.currentTime;

  try {
    if (typeof parameter.cancelAndHoldAtTime === "function") {
      parameter.cancelAndHoldAtTime(now);
    } else {
      const current = parameter.value;
      parameter.cancelScheduledValues(now);
      parameter.setValueAtTime(current, now);
    }

    parameter.setTargetAtTime(safeVolume, now, 0.012);
    parameter.setValueAtTime(safeVolume, now + 0.08);
  } catch {
    try {
      element.volume = safeVolume;
      element.muted = safeVolume <= 0;
    } catch {}
  }
}

fadeOut(id, duration = 2000) {
  const entry = this.elements.get(id);
  if (!entry) return;

  const element = entry.element;
  const safeDuration = Math.max(150, Number(duration) || 2000);

  entry.targetVolume = 0;

  if (entry.fadeRaf) {
    cancelAnimationFrame(entry.fadeRaf);
    entry.fadeRaf = null;
  }

  if (this.nativeMode || entry.forceNative || !entry.gain || !this.context) {
    if (!element) return;

    let startVolume = 0;

    try {
      startVolume = element.muted ? 0 : Math.max(0, Math.min(1, Number(element.volume) || 0));
      element.muted = false;
    } catch {}

    const startedAt = performance.now();

    const step = (now) => {
      const progress = Math.min(1, (now - startedAt) / safeDuration);
      const volume = startVolume * Math.cos(progress * Math.PI * 0.5);

      try {
        element.muted = false;
        element.volume = Math.max(0, Math.min(1, volume));
      } catch {}

      if (progress < 1) {
        entry.fadeRaf = requestAnimationFrame(step);
        return;
      }

      entry.fadeRaf = null;

      try {
        element.volume = 0;
        element.muted = true;
      } catch {}
    };

    entry.fadeRaf = requestAnimationFrame(step);
    return;
  }

  const parameter = entry.gain.gain;
  const now = this.context.currentTime;
  const seconds = safeDuration / 1000;

  try {
    if (typeof parameter.cancelAndHoldAtTime === "function") {
      parameter.cancelAndHoldAtTime(now);
    } else {
      const heldValue = parameter.value;
      parameter.cancelScheduledValues(now);
      parameter.setValueAtTime(heldValue, now);
    }

    const current = Math.max(0, Math.min(1, Number(parameter.value) || 0));
    const curve = new Float32Array(128);

    for (let index = 0; index < curve.length; index += 1) {
      const progress = index / (curve.length - 1);
      curve[index] = current * Math.cos(progress * Math.PI * 0.5);
    }

    parameter.setValueCurveAtTime(curve, now, seconds);
    parameter.setValueAtTime(0, now + seconds);
  } catch {
    this.setVolume(id, 0, safeDuration);
  }
}

  async play(audioElement, maxRetries = 2) {
    if (!audioElement) return false;
    const requestId = Symbol("play");
    this._playRequests ??= new WeakMap();
    this._playRequests.set(audioElement, requestId);
    const operation = getMediaOperationToken(audioElement);
    const isCurrent = () => this._playRequests.get(audioElement) === requestId && isMediaOperationCurrent(operation);
    this._normalizePlaybackRate(audioElement);

    for (let i = 0; i < maxRetries; i++) {
      try {
        await refreshAudioSource(audioElement);
        if (!isCurrent()) return false;
        await this.unlock();
        if (!isCurrent()) return false;

        if (!this.nativeMode && this.context) {
          const st = this.context.state;
          if (st === "suspended" || st === "interrupted") {
            await this.context.resume();
            if (!isCurrent()) return false;
          }
if (this._useIOSStreamOutput) {
  const sinkStarted = await this._ensureStreamSinkPlaying();

  if (!sinkStarted) {
    throw new Error("La sortie audio iOS n’a pas démarré.");
  }
  if (!isCurrent()) return false;
}
        }

        if (!isCurrent()) return false;
        const p = audioElement.play();
        if (p) await p;
        if (!isCurrent()) { if (this._playRequests.get(audioElement) === requestId) { try { audioElement.pause(); } catch {} } return false; }
        return true;
      } catch (err) {
        console.warn(`⚠️ Play attempt ${i + 1}/${maxRetries} failed:`, err?.name || err);

        if (i < maxRetries - 1) {
          await new Promise((r) => setTimeout(r, 150));
          if (!isCurrent()) return false;

          /* load() remet currentTime à zéro : réservé au cas où l'élément
             n'a réellement aucune donnée, sinon la reprise repart du début. */
          if (audioElement.readyState === 0) {
            try { audioElement.load(); } catch {}
          }
        }
      }
    }

    return false;
  }

  cancelPendingPlay(element) {
    if (!element) return;
    this._playRequests ??= new WeakMap();
    this._playRequests.set(element, Symbol("cancelled"));
    cancelMediaOperation(element);
  }

  cleanup(id, opts = {}) {
    const { hard = false } = opts;

    const entry = this.elements.get(id);
    if (!entry) return;

    if (entry.fadeRaf) {
      cancelAnimationFrame(entry.fadeRaf);
      entry.fadeRaf = null;
    }

    const el = entry.element;
    this.cancelPendingPlay(el);
    this._detachStreamActivityListeners(el);

    // stop element
    if (el) {
      try {
        el.pause();
        el.currentTime = 0;
      } catch {}
    }

// Disconnect WebAudio UNIQUEMENT en hard cleanup
// IMPORTANT : ne jamais supprimer source/gain/ctx de el._audioManager.
// Un HTMLMediaElement ne peut avoir qu'un seul MediaElementSourceNode
// pendant sa vie. Si on supprime les refs JS, AudioManager tentera de
// refaire createMediaElementSource(audioElement), ce que le navigateur refuse.
if (hard && el?._audioManager?.gain) {
  try {
    el._audioManager.gain.disconnect();
  } catch {}

  // On garde :
  // - el._audioManager.source
  // - el._audioManager.gain
  // - el._audioManager.ctx
  //
  // setupElement pourra ensuite réutiliser le câblage existant et reconnecter
  // le gain vers la bonne sortie.
}

// Reset src UNIQUEMENT en hard cleanup
if (hard && el) {
  this.setVolume(id, 0, 0);

  try {
    el.removeAttribute("src");
    el.load();
  } catch {}
}

    this.elements.delete(id);

    // si plus rien ne joue, on peut stopper le sink
    if (this._useIOSStreamOutput && this._streamSink) {
      const anyPlaying = [...this.elements.values()].some((e) => {
        const a = e.element;
        return a && !a.paused && !a.ended;
      });
      if (!anyPlaying && !this._streamSink.paused) {
        try { this._streamSink.pause(); } catch {}
      }
    }
  }

  cleanupAll() {
    [...this.elements.keys()].forEach((id) => this.cleanup(id, { hard: true }));
    this.elements.clear();

    // Nettoyage iOS stream sink
    if (this._streamSink) {
      try { this._streamSink.pause(); } catch {}
      try { this._streamSink.srcObject = null; } catch {}
      try { this._streamSink.remove(); } catch {}
      this._streamSink = null;
    }

    if (this._streamDest) {
      try {
        this._streamDest.stream?.getTracks?.().forEach((t) => t.stop());
      } catch {}
      this._streamDest = null;
    }
  }

  // Resync après visibility change
  resyncVolume(id) {
    const entry = this.elements.get(id);
    if (!entry) return;
    this.setVolume(id, entry.targetVolume, 0);
  }
}

export const audioManager = new AudioManager();